import React, { createContext, useCallback, useContext, useMemo, useState } from "react";
import type { StickyEntity } from "../types/devops";

interface DevopsWorkspaceContextValue {
  stickyEntity: StickyEntity | null;
  agentOpen: boolean;
  bindEntity: (entity: StickyEntity) => void;
  unbindEntity: () => void;
  openAgent: () => void;
  closeAgent: () => void;
}

const DevopsWorkspaceContext = createContext<DevopsWorkspaceContextValue | null>(null);

export const DevopsWorkspaceProvider: React.FC<{ children: React.ReactNode }> = ({
  children,
}) => {
  const [stickyEntity, setStickyEntity] = useState<StickyEntity | null>(null);
  const [agentOpen, setAgentOpen] = useState(false);
  const openAgent = useCallback(() => setAgentOpen(true), []);
  const closeAgent = useCallback(() => setAgentOpen(false), []);
  const bindEntity = useCallback((entity: StickyEntity) => {
    setStickyEntity(entity);
    setAgentOpen(true);
  }, []);
  const unbindEntity = useCallback(() => setStickyEntity(null), []);
  const value = useMemo(
    () => ({
      stickyEntity,
      agentOpen,
      bindEntity,
      unbindEntity,
      openAgent,
      closeAgent,
    }),
    [stickyEntity, agentOpen, bindEntity, unbindEntity, openAgent, closeAgent],
  );
  return (
    <DevopsWorkspaceContext.Provider value={value}>{children}</DevopsWorkspaceContext.Provider>
  );
};

export function useDevopsWorkspace() {
  const ctx = useContext(DevopsWorkspaceContext);
  if (!ctx) {
    throw new Error("useDevopsWorkspace must be used within DevopsWorkspaceProvider");
  }
  return ctx;
}
